import { Link, useNavigate } from "react-router";

import { Button } from "@/components/ui/button";
import { useHealth, useLogout, useMe } from "@/lib/api";

// Constant-length JSX expression keeps prettier output stable for any rendered name.
const PROJECT_NAME = "Vogsy-BOI-mcp-test";

export function HomePage() {
  const navigate = useNavigate();
  const logoutMutation = useLogout();
  const { data: health } = useHealth();
  const { data: me } = useMe();

  const backendStatus = health?.status ?? "unreachable";

  const handleLogout = async () => {
    await logoutMutation.mutateAsync();
    navigate("/login");
  };

  return (
    <div className="bg-background min-h-screen">
      <header className="border-border flex items-center justify-between border-b px-6 py-4">
        <div>
          <p className="text-muted text-[11px] font-medium tracking-[0.3em] uppercase">
            Board of Innovation
          </p>
          <h1 className="font-heading text-primary text-xl font-normal tracking-[-0.03em]">
            {PROJECT_NAME}
          </h1>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/profile">
            <Button variant="ghost" size="sm">
              Profile
            </Button>
          </Link>
          {me?.role === "admin" && (
            <>
              <Link to="/admin/users">
                <Button variant="ghost" size="sm">
                  Admin
                </Button>
              </Link>
              <Link to="/admin/activity">
                <Button variant="ghost" size="sm">
                  Activity
                </Button>
              </Link>
            </>
          )}
          <Button variant="outline" onClick={handleLogout}>
            Sign out
          </Button>
        </div>
      </header>
      <main className="mx-auto max-w-6xl px-6 py-12">
        <section className="grid gap-8 lg:grid-cols-[1.4fr_0.8fr]">
          <div className="bg-primary text-primary-foreground relative overflow-hidden rounded-3xl px-8 py-10 md:px-10 md:py-12">
            <div className="relative z-10 max-w-2xl space-y-6">
              <p className="text-accent-light text-xs font-medium tracking-[0.35em] uppercase">
                Internal Slack operations
              </p>
              <h2 className="font-heading text-4xl leading-[0.95] font-normal tracking-[-0.05em] md:text-6xl">
                Keep reminder flows crisp and project status visible.
              </h2>
              <p className="text-accent-light max-w-xl text-sm leading-7 md:text-base">
                Designed for BOI teams managing MCP-enabled Slack touchpoints, this connector helps
                you monitor delivery signals, trigger reminders, and move fast without adding
                operational clutter.
              </p>
            </div>
            <div className="absolute right-8 bottom-0 flex gap-2">
              <span className="bg-accent h-24 w-4 md:h-32" />
              <span className="bg-accent/80 h-24 w-4 md:h-32" />
              <span className="bg-accent/55 h-24 w-4 md:h-32" />
              <span className="bg-accent/25 h-24 w-4 md:h-32" />
            </div>
          </div>
          <div className="border-border bg-card rounded-3xl border p-8">
            <p className="text-muted text-[11px] font-medium tracking-[0.3em] uppercase">
              System pulse
            </p>
            <div className="mt-6 space-y-6">
              <div>
                <p className="text-primary text-4xl font-normal tracking-[-0.04em]">Live</p>
                <p className="text-muted mt-2 text-sm leading-6">
                  Workspace access, reminder orchestration, and status review in one focused
                  internal tool.
                </p>
              </div>
              <div className="border-border border-t pt-6">
                <p className="text-muted text-xs tracking-[0.24em] uppercase">Backend status</p>
                <p className="mt-2 text-sm">
                  <span className={backendStatus === "ok" ? "text-primary" : "text-destructive"}>
                    {backendStatus}
                  </span>
                </p>
              </div>
            </div>
          </div>
        </section>
        <section className="mt-8 grid gap-6 md:grid-cols-3">
          <div className="border-border rounded-2xl border bg-transparent p-6">
            <p className="text-muted text-[11px] font-medium tracking-[0.3em] uppercase">
              Reminder cadence
            </p>
            <p className="text-primary mt-4 text-xl font-medium">
              Trigger follow-ups with less manual checking.
            </p>
          </div>
          <div className="border-border rounded-2xl border bg-transparent p-6">
            <p className="text-muted text-[11px] font-medium tracking-[0.3em] uppercase">
              Project visibility
            </p>
            <p className="text-primary mt-4 text-xl font-medium">
              Surface status changes before they become blockers.
            </p>
          </div>
          <div className="border-border rounded-2xl border bg-transparent p-6">
            <p className="text-muted text-[11px] font-medium tracking-[0.3em] uppercase">
              Internal tooling
            </p>
            <p className="text-primary mt-4 text-xl font-medium">
              Built for a minimalist, high-contrast BOI workflow.
            </p>
          </div>
        </section>
        <p className="text-muted mt-8 text-sm">
          Backend status:{" "}
          <span className={backendStatus === "ok" ? "text-primary" : "text-destructive"}>
            {backendStatus}
          </span>
        </p>
      </main>
    </div>
  );
}
